import { useQuery } from '@tanstack/react-query'
import { 
  ChartBarIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { APIMetrics } from '@/types'
import { fetchMetrics } from '@/services/api'

const mockMetrics: APIMetrics = {
  totalRequests: 125430,
  successRate: 99.2,
  avgResponseTime: 145,
  requestsToday: 8420,
  errorsToday: 12,
  topEndpoints: [
    { endpoint: '/v1/users', requests: 45230, avgResponseTime: 120 },
    { endpoint: '/v1/products', requests: 32100, avgResponseTime: 180 },
    { endpoint: '/v1/orders', requests: 28900, avgResponseTime: 95 },
  ]
}

const requestsData = [
  { name: 'Mon', requests: 12000, errors: 45 },
  { name: 'Tue', requests: 15000, errors: 32 },
  { name: 'Wed', requests: 18000, errors: 28 },
  { name: 'Thu', requests: 22000, errors: 15 },
  { name: 'Fri', requests: 25000, errors: 8 },
  { name: 'Sat', requests: 19000, errors: 12 },
  { name: 'Sun', requests: 14000, errors: 18 },
]

const responseTimeData = [
  { time: '00:00', responseTime: 120 },
  { time: '04:00', responseTime: 95 },
  { time: '08:00', responseTime: 180 },
  { time: '12:00', responseTime: 220 },
  { time: '16:00', responseTime: 165 },
  { time: '20:00', responseTime: 140 },
]

export default function Dashboard() {
  const { data: metrics = mockMetrics, isLoading } = useQuery({
    queryKey: ['metrics'],
    queryFn: fetchMetrics,
  })

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-5">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const stats = [
    {
      name: 'Total Requests',
      value: metrics.totalRequests.toLocaleString(),
      icon: ChartBarIcon,
      change: '+12%',
      changeType: 'increase' as const,
    },
    {
      name: 'Success Rate',
      value: `${metrics.successRate}%`,
      icon: ArrowUpIcon,
      change: '+0.3%',
      changeType: 'increase' as const,
    },
    {
      name: 'Avg Response Time',
      value: `${metrics.avgResponseTime}ms`,
      icon: ClockIcon,
      change: '-5ms',
      changeType: 'decrease' as const,
    },
    {
      name: 'Errors Today',
      value: metrics.errorsToday.toString(),
      icon: ExclamationTriangleIcon,
      change: '-8',
      changeType: 'decrease' as const,
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Overview of your API performance and usage metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.changeType === 'increase' ? (
                        <ArrowUpIcon className="h-4 w-4 flex-shrink-0 self-center" />
                      ) : (
                        <ArrowDownIcon className="h-4 w-4 flex-shrink-0 self-center" />
                      )}
                      <span className="sr-only">
                        {stat.changeType === 'increase' ? 'Increased' : 'Decreased'} by
                      </span>
                      {stat.change}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Requests Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Weekly Requests & Errors
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={requestsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="requests" fill="#3b82f6" name="Requests" />
                <Bar dataKey="errors" fill="#ef4444" name="Errors" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Response Time Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Response Time (24h)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={responseTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="responseTime" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Response Time (ms)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Endpoints Table */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Endpoints</h3>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Response Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics.topEndpoints.map((endpoint, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {endpoint.endpoint}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {endpoint.requests.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {endpoint.avgResponseTime}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
